// ============================================================================
// QuickLinksCard — liens rapides personnels (rail accueil)
// ============================================================================
// Titre de section parent (gest:accueil.rail.*) ; cette carte n'a plus d'en-tete
// RailCard propre. Mon equipe gate capability-aware (M-150).
//
// Tuiles figees par Marc-Antoine le 2026-10-29 a 21 h 36 (lot
// design-gest-les-acces-rapides-accrochent). Regle « brand = wayfinding »
// (UI_STANDARDS §Regle pro FIGEE) : la marque en ACCENT seulement (pastille
// d'icone, barre de survol, focus), aucun degrade, aucun fond plein de marque.

import { useId } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { User, Users, Wallet } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { RailCard } from './RailCard';

interface QuickLink {
  to: string;
  label: string;
  hint: string;
  /** Id de la ligne d'aide : le lien la porte en description, son nom reste le libelle. */
  hintId: string;
  icon: LucideIcon;
}

/**
 * Tuile. Sous 18rem de carte (rail etroit des ecrans 768-1024 px : ~190 px mesures),
 * trois tuiles coupaient « Ma remuneration » : la tuile s'y couche en rangee
 * (icone a gauche) ; des 18rem (telephone 301 px, bureau 360 px), grille verticale.
 */
const TILE =
  'group relative flex w-full touch-manipulation items-center gap-3 overflow-hidden ' +
  'rounded-xl border border-slate-200 bg-white px-3 py-3 text-left ' +
  '@2xs:flex-col @2xs:gap-2.5 @2xs:px-2 @2xs:pb-4 @2xs:pt-4 @2xs:text-center ' +
  'transition duration-200 ease-out hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md ' +
  'motion-reduce:transition-none motion-reduce:hover:translate-y-0 ' +
  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 ' +
  'dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-slate-600 dark:focus-visible:ring-offset-slate-800';

/**
 * Pastille d'icone teintee de la marque (cran 50/100 = pastille, §4c). Icone au
 * cran 800 : >= 4:1 sur le cran 50 meme pour une marque jaune (mesure deriveBrandScale :
 * #FFD200 = 4,10:1 au cran 800 contre 2,62:1 au cran 700 ; UG #F99D27 = 5,37:1).
 */
const ICON_CHIP =
  'flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-800 ring-1 ring-brand-100 ' +
  'transition-transform duration-200 ease-out group-hover:scale-110 motion-reduce:transition-none ' +
  'motion-reduce:group-hover:scale-100 dark:bg-brand-500/15 dark:text-brand-300 dark:ring-brand-500/25';

/** Barre de marque qui se deploie sous la tuile au survol et au focus (accent, jamais un fond). */
const HOVER_BAR =
  'absolute inset-x-0 bottom-0 h-0.75 origin-left scale-x-0 bg-brand-500 transition-transform ' +
  'duration-200 ease-out group-hover:scale-x-100 group-focus-visible:scale-x-100 motion-reduce:transition-none';

export interface QuickLinksCardProps {
  /** M-150 : true apres probe GET /api/mon-equipe 200 (director|admin). */
  canViewMonEquipe: boolean;
  className?: string;
}

export function QuickLinksCard({ canViewMonEquipe, className }: QuickLinksCardProps) {
  const { t } = useTranslation();
  const idBase = useId();

  const links: QuickLink[] = [
    {
      to: '/profile',
      label: t('gest:accueil.quickLinks.myProfile'),
      hint: t('gest:accueil.quickLinks.myProfileHint'),
      hintId: `${idBase}-profil`,
      icon: User,
    },
    {
      to: '/profile?tab=remuneration',
      label: t('gest:accueil.quickLinks.myCompensation'),
      hint: t('gest:accueil.quickLinks.myCompensationHint'),
      hintId: `${idBase}-remuneration`,
      icon: Wallet,
    },
  ];
  if (canViewMonEquipe) {
    links.push({
      to: '/mon-equipe',
      label: t('gest:accueil.quickLinks.myTeam'),
      hint: t('gest:accueil.quickLinks.myTeamHint'),
      hintId: `${idBase}-equipe`,
      icon: Users,
    });
  }

  return (
    <RailCard className={className}>
      <nav aria-label={t('gest:accueil.quickLinks.ariaLabel')} className="@container">
        <ul className={`grid grid-cols-1 gap-2.5 ${links.length >= 3 ? '@2xs:grid-cols-3' : '@2xs:grid-cols-2'}`}>
          {links.map(({ to, label, hint, hintId, icon: Icon }) => (
            <li key={to} className="flex">
              <Link to={to} aria-label={label} aria-describedby={hintId} className={TILE}>
                <span className={ICON_CHIP} aria-hidden="true">
                  <Icon size={24} />
                </span>
                <span className="flex min-w-0 flex-col gap-0.5 @2xs:gap-1.5">
                  <span className="text-body font-semibold leading-tight text-slate-800 dark:text-slate-100">
                    {label}
                  </span>
                  <span id={hintId} className="line-clamp-2 text-meta text-slate-500 dark:text-slate-400">
                    {hint}
                  </span>
                </span>
                <span aria-hidden="true" className={HOVER_BAR} />
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </RailCard>
  );
}
